import { Ref, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import _uniqBy from 'lodash/uniqBy'
import {
  ChartingLibraryWidgetOptions,
  CustomIndicator,
  LanguageCode,
  PineJS,
  RawStudyMetaInfoId,
  widget,
} from '../../../charting_library'
import DATAFEED from './datafeed'
import { RANK_COLORS } from '../constant'
import {
  addCustomFullScreenButton,
  checkExternalFeatureParams,
  defaultChartWidgetOptions,
} from '../../function'
import { useLazyQuery, useSubscription } from '@apollo/client'
import { GET_STOCK_RATING } from '../../../graphQL/queries/rating'
import { SUB_STOCKS_RATING } from '../../../graphQL/subscription/rating'
import moment from 'moment'
import Cookies from 'js-cookie'

const RatingChartContainer = () => {
  const socketData = useRef<any>({})
  const widgetRef = useRef<any>()
  const ref: Ref<any> = useRef()
  const storeData = useRef<any[]>([])
  const dataFeedFunc = useRef<any>({})
  const [searchParams, _] = useSearchParams()
  const accessToken = searchParams.get('accessToken')
  const symbols = searchParams.get('symbols')?.split(',') || []
  const locale = (searchParams.get('language') as LanguageCode) || 'vi'
  const type = searchParams.get('type') || 'POINT'
  const periodRef = useRef({
    to: moment().unix(),
    from: moment().subtract(6, 'months').unix(),
  })

  const [fetchData] = useLazyQuery(GET_STOCK_RATING, {
    context: {
      headers: { Authorization: accessToken ? `Bearer ${accessToken}` : '' },
    },
  })

  useSubscription(SUB_STOCKS_RATING, {
    variables: {},
    onData: ({
      data: {
        data: {
          stocksRating: { data },
        },
      },
    }) => {
      if (storeData.current.length > 0 && dataFeedFunc.current?.onTick) {
        const firstRank = data.find((a: any) => a.code === symbols[0])

        if (firstRank) {
          const value =
            type === 'POINT' ? firstRank.totalPoints : firstRank.rank

          data.map((a: any) => {
            if (a.code !== symbols[0]) {
              socketData.current[a.code] =
                type === 'POINT' ? a.totalPoints : a.rank
            }
          })

          dataFeedFunc.current?.onTick({
            time: moment().unix() * 1000,
            close: value,
            open: value,
            high: value,
            low: value,
            volume: value,
          })
        }
      }
    },
  })

  const customIndicatorsGetter = (PineJS: PineJS) => {
    const items = symbols.slice(1, 10).map((symbol, index) => {
      return {
        name: `StockRank${symbol}`,
        metainfo: {
          _metainfoVersion: 51,
          id: `stock-${symbol}@tv-basicstudies-1` as RawStudyMetaInfoId,
          name: symbol,
          description: symbol,
          shortDescription: symbol,
          is_hidden_study: false,
          is_price_study: true,
          isCustomIndicator: true,
          format: {
            type: 'price',
            precision: 0,
          },
          plots: [{ id: 'plot_0', type: 'line' as any }],
          defaults: {
            styles: {
              plot_0: {
                linestyle: 0,
                visible: true,
                linewidth: 2,
                plottype: 2 as any,
                trackPrice: false,
                color: RANK_COLORS[index + 1],
              },
            },
            inputs: {},
          },
          styles: {
            plot_0: {
              title: symbol,
              histogramBase: 0,
            },
          },
          inputs: [],
        },
        constructor: function () {
          const mainThis = this as any
          mainThis.init = function (context: unknown, inputCallback: never) {
            this._context = context
            this._input = inputCallback
          }
          mainThis.main = function (context: unknown, inputCallback: never) {
            this._context = context
            this._input = inputCallback

            if (storeData.current.length > 0 && socketData.current[symbol]) {
              return [socketData.current[symbol]]
            }

            const time = PineJS.Std.time(this._context)
            const value = _uniqBy(storeData.current, 'time').find(
              (a: any) => String(a.time) === String(time),
            ) as any

            return value && value[symbol] ? [value[symbol]] : []
          }
        },
      } as CustomIndicator
    })

    return Promise.resolve<CustomIndicator[]>(items)
  }

  useEffect(() => {
    if (!ref.current) return

    if (accessToken) Cookies.set('access_token', accessToken)

    const widgetOptions: ChartingLibraryWidgetOptions = {
      ...defaultChartWidgetOptions,
      symbol: symbols[0],
      datafeed: DATAFEED({
        dataFeedFunc,
        storeData,
        fetchData,
        symbols: symbols.slice(0, 10),
        type,
        widgetRef,
        periodRef,
      }),
      container: ref.current,
      locale,
      disabled_features: [
        'use_localstorage_for_settings',
        'header_symbol_search',
        'symbol_search_hot_key',
        'header_chart_type',
        'header_resolutions',
      ],
      enabled_features: [
        'study_templates',
        'pinch_scale',
        'show_zoom_and_move_buttons_on_touch',
        'vert_touch_drag_scroll',
        'horz_touch_drag_scroll',
      ],
      timeframe: '3m',
      custom_indicators_getter: customIndicatorsGetter,
      time_frames: [
        {
          text: '2y',
          resolution: '1D' as ChartingLibraryWidgetOptions['interval'],
          description: '2 Years',
          title: '2Y',
        },
        {
          text: '1y',
          resolution: '1D' as ChartingLibraryWidgetOptions['interval'],
          description: '1 Years',
          title: '1Y',
        },
        {
          text: '6m',
          resolution: '1D' as ChartingLibraryWidgetOptions['interval'],
          description: '6 Months',
          title: '6M',
        },
        {
          text: '3m',
          resolution: '1D' as ChartingLibraryWidgetOptions['interval'],
          description: '3 Months',
          title: '3M',
        },
        {
          text: '1m',
          resolution: '1D' as ChartingLibraryWidgetOptions['interval'],
          description: '1 Months',
          title: '1M',
        },
      ],
    }

    const tvWidget = new widget(
      checkExternalFeatureParams(searchParams, widgetOptions),
    )

    tvWidget.headerReady().then(() => {
      widgetRef.current = tvWidget
      if (searchParams.get('custom-fullscreen'))
        addCustomFullScreenButton(tvWidget, 'FULL_SCREEN_MODE_STOCK')
    })

    tvWidget.onChartReady(() => {
      tvWidget.chart().setChartType(2)
      tvWidget.chart().executeActionById('lineHide')
      tvWidget.chart().removeAllStudies()
      tvWidget.chart().getSeries().setChartStyleProperties(2, {
        color: RANK_COLORS[0],
      })

      symbols.slice(1, 10).forEach((sb) => {
        tvWidget.chart().createStudy(sb, false, false, {})
      })
    })

    return () => {
      if (tvWidget) tvWidget.remove()
    }
  }, [searchParams])

  return (
    <div ref={ref} className={'TVChartContainer'} style={{ height: '100%' }} />
  )
}

export default RatingChartContainer
